import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button } from '../shared';
import { pollForecastStatus } from '../../utils/api';
import { STATUS_LABELS, AGENTS } from '../../config';
import './ForecastProcess.css';

function ForecastProcess() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [forecast, setForecast] = useState(null);
  const [error, setError] = useState(null);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const runPolling = async () => {
      try {
        const result = await pollForecastStatus(id, (update) => {
          if (isMounted) {
            setForecast(update);
            // Check for completion on each update
            if (update.status === 'complete') {
              setIsComplete(true);
            } else if (update.status === 'error') {
              setError(update.error || 'An error occurred during processing');
            }
          }
        });

        if (isMounted) {
          setForecast(result);
          if (result.status === 'complete') {
            setIsComplete(true);
          } else if (result.status === 'error') {
            setError(result.error || 'An error occurred during processing');
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message);
        }
      }
    };

    runPolling();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleViewResults = () => {
    navigate(`/forecast/${id}`);
  };

  const getProgressPercentage = () => {
    if (!forecast?.status) return 0;
    const stages = ['pending', 'round_0', 'searching', 'round_1', 'mediator_1', 'round_2', 'mediator_2', 'round_3', 'aggregating', 'complete'];
    const currentIndex = stages.indexOf(forecast.status);
    return Math.max(0, ((currentIndex + 1) / stages.length) * 100);
  };

  const renderAgentStatus = (agentId, roundData) => {
    if (!roundData?.forecasts) return null;
    const agentForecast = roundData.forecasts.find(f => f.agent_id === agentId);
    const agent = AGENTS[agentId];

    return (
      <div className="agent-status" key={agentId}>
        <div className="agent-status-header">
          <span
            className="agent-dot"
            style={{ backgroundColor: agent.color }}
          />
          <span className="agent-name">{agent.name}</span>
          <span className="agent-model text-tertiary">{agent.model}</span>
        </div>
        {agentForecast && (
          <div className="agent-forecast">
            <span className="forecast-value">
              {(agentForecast.mean * 100).toFixed(1)}%
            </span>
            <span className="forecast-std text-tertiary">
              ± {(agentForecast.std_dev * 100).toFixed(1)}%
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="forecast-process-page">
      <div className="forecast-process-container">
        <header className="forecast-process-header">
          <h1 className="forecast-process-title">Processing Forecast</h1>
          {forecast?.market_title && (
            <p className="forecast-process-question">{forecast.market_title}</p>
          )}
        </header>

        {error ? (
          <Card className="error-card" variant="bordered">
            <h3>Error</h3>
            <p>{error}</p>
            <Button onClick={() => navigate('/')} variant="secondary">
              Start Over
            </Button>
          </Card>
        ) : (
          <>
            <Card className="progress-card" variant="elevated">
              <div className="progress-header">
                <span className="progress-status">
                  {STATUS_LABELS[forecast?.status] || 'Initializing...'}
                </span>
                <span className="progress-percent">
                  {Math.round(getProgressPercentage())}%
                </span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${getProgressPercentage()}%` }}
                />
              </div>
            </Card>

            {forecast?.round_1 && (
              <Card className="round-card">
                <h3 className="round-title">Round 1: Initial Forecasts</h3>
                <div className="agents-grid">
                  {Object.keys(AGENTS).map(agentId =>
                    renderAgentStatus(agentId, forecast.round_1)
                  )}
                </div>
              </Card>
            )}

            {forecast?.mediator_1 && (
              <Card className="mediator-card">
                <h3 className="round-title">Mediator Analysis 1</h3>
                <p className="mediator-summary">{forecast.mediator_1.summary}</p>
              </Card>
            )}

            {forecast?.round_2 && (
              <Card className="round-card">
                <h3 className="round-title">Round 2: With Full Information</h3>
                <div className="agents-grid">
                  {Object.keys(AGENTS).map(agentId =>
                    renderAgentStatus(agentId, forecast.round_2)
                  )}
                </div>
              </Card>
            )}

            {forecast?.mediator_2 && (
              <Card className="mediator-card">
                <h3 className="round-title">Mediator Analysis 2</h3>
                <p className="mediator-summary">{forecast.mediator_2.summary}</p>
              </Card>
            )}

            {forecast?.round_3 && (
              <Card className="round-card">
                <h3 className="round-title">Round 3: Final Forecasts</h3>
                <div className="agents-grid">
                  {Object.keys(AGENTS).map(agentId =>
                    renderAgentStatus(agentId, forecast.round_3)
                  )}
                </div>
              </Card>
            )}

            {isComplete && (
              <div className="complete-section">
                <Card className="final-card" variant="elevated">
                  <h3 className="final-title">Final Forecast</h3>
                  {forecast?.final_forecast && (
                    <div className="final-forecast">
                      <span className="final-value">
                        {(forecast.final_forecast.mean * 100).toFixed(1)}%
                      </span>
                      <span className="final-std text-secondary">
                        ± {(forecast.final_forecast.std_dev * 100).toFixed(1)}%
                      </span>
                    </div>
                  )}
                </Card>
                <Button onClick={handleViewResults} variant="primary">
                  View Full Results
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ForecastProcess;
